import { createClient } from '@/lib/supabase/client';
import { FormlessItem, FormStatus } from '@/app/workspace-dashboard/components/WorkspaceContent';

// ─── Type helpers ─────────────────────────────────────────────────────────────

function rowToFormlessItem(row: Record<string, unknown>): FormlessItem {
  return {
    id: row.id as string,
    title: row.title as string,
    description: (row.description as string) || '',
    status: (row.form_status as FormStatus) || 'draft',
    responseCount: (row.response_count as number) || 0,
    completionRate: (row.completion_rate as number) || 0,
    lastActivity: (row.last_activity as string) || 'Never',
    createdAt: row.created_at
      ? new Date(row.created_at as string).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Unknown',
    hasPattern: (row.has_pattern as boolean) || false,
    patternChar: (row.pattern_char as string) || undefined,
    bgColor: (row.bg_color as string) || '#111111',
    workspaceId: (row.workspace_id as string) || undefined,
  };
}

// ─── Workspaces ───────────────────────────────────────────────────────────────

export interface Workspace {
  id: string;
  name: string;
  userId: string;
  createdAt: string;
}

function rowToWorkspace(row: Record<string, unknown>): Workspace {
  return {
    id: row.id as string,
    name: row.name as string,
    userId: row.user_id as string,
    createdAt: row.created_at
      ? new Date(row.created_at as string).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Unknown',
  };
}

export const workspaceService = {
  async getWorkspaces(userId: string): Promise<Workspace[]> {
    console.log('[formService] getWorkspaces called for userId:', userId);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('workspaces')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[formService] getWorkspaces error:', error.message);
      return [];
    }
    console.log('[formService] getWorkspaces completed, found:', data?.length || 0, 'workspaces');
    return (data || []).map(rowToWorkspace);
  },

  async createWorkspace(userId: string, name: string = 'New workspace'): Promise<Workspace | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('workspaces')
      .insert({ user_id: userId, name })
      .select()
      .single();

    if (error) {
      console.error('createWorkspace error:', error.message);
      return null;
    }
    return rowToWorkspace(data);
  },

  async renameWorkspace(workspaceId: string, name: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('workspaces')
      .update({ name, updated_at: new Date().toISOString() })
      .eq('id', workspaceId);

    if (error) {
      console.error('renameWorkspace error:', error.message);
      return false;
    }
    return true;
  },

  async deleteWorkspace(workspaceId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('workspaces')
      .delete()
      .eq('id', workspaceId);

    if (error) {
      console.error('deleteWorkspace error:', error.message);
      return false;
    }
    return true;
  },

  async ensureDefaultWorkspace(userId: string): Promise<Workspace | null> {
    console.log('[formService] ensureDefaultWorkspace called for userId:', userId);
    const existing = await workspaceService.getWorkspaces(userId);
    console.log('[formService] Existing workspaces:', existing.length);
    if (existing.length > 0) {
      console.log('[formService] Returning existing workspace:', existing[0].id);
      return existing[0];
    }
    console.log('[formService] Creating default workspace...');
    const created = await workspaceService.createWorkspace(userId, 'Main Space');
    console.log('[formService] Created workspace:', created?.id);
    return created;
  },
};

// ─── Forms CRUD ───────────────────────────────────────────────────────────────

export const formService = {
  async getForms(userId: string, workspaceId?: string): Promise<FormlessItem[]> {
    console.log('[formService] getForms called, userId:', userId, 'workspaceId:', workspaceId);
    const supabase = createClient();
    let query = supabase
      .from('forms')
      .select('*, conversations(id)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (workspaceId) {
      query = query.eq('workspace_id', workspaceId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[formService] getForms error:', error.message);
      return [];
    }
    console.log('[formService] getForms completed, found:', data?.length || 0, 'forms');
    return (data || []).map((row) => {
      const conversationCount = Array.isArray(row.conversations) ? row.conversations.length : 0;
      return {
        ...rowToFormlessItem(row),
        responseCount: conversationCount,
      };
    });
  },

  async createForm(userId: string, workspaceId?: string): Promise<FormlessItem | null> {
    const supabase = createClient();
    const id = `f${Date.now()}`;
    const defaultConfig = {
      prompt: '',
      schema: [],
      tone: 'professional',
      customTone: '',
      rules: [
        { id: 'r1', label: 'Ask one question at a time', enabled: true },
        { id: 'r2', label: 'Validate email format', enabled: true },
        { id: 'r3', label: 'Allow skipping optional fields', enabled: true },
        { id: 'r4', label: 'Confirm answers before proceeding', enabled: false },
      ],
      aiProvider: { provider: 'openai', apiKey: '', model: 'gpt-4o-mini', temperature: 0.7, maxTokens: 1024 },
      renderConfig: {
        background: '#0a0a0a',
        backgroundType: 'solid',
        gradientFrom: '#0a0a0a',
        gradientTo: '#1a1a2e',
        font: 'Manrope',
        questionFontSize: 32,
        buttonStyle: 'pill',
        buttonBg: '#ffffff',
        buttonText: '#0a0a0a',
        layout: 'centered',
        buttonSize: 'medium',
        buttonTextSize: 16,
        loadingBackground: 'light',
      },
      systemPrompt: '',
      isPublic: false,
    };

    const insertData: Record<string, unknown> = {
      id,
      user_id: userId,
      title: 'Untitled Form',
      description: '',
      form_status: 'draft',
      response_count: 0,
      completion_rate: 0,
      last_activity: 'Never',
      has_pattern: false,
      bg_color: '#111111',
      config: defaultConfig,
    };

    if (workspaceId) {
      insertData.workspace_id = workspaceId;
    }

    const { data, error } = await supabase
      .from('forms')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('createForm error:', error.message);
      return null;
    }
    return rowToFormlessItem(data);
  },

  async deleteForm(formId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('forms')
      .delete()
      .eq('id', formId);

    if (error) {
      console.error('deleteForm error:', error.message);
      return false;
    }
    return true;
  },

  async duplicateForm(form: FormlessItem, userId: string): Promise<FormlessItem | null> {
    const supabase = createClient();
    const newId = `f${Date.now()}`;
    const { data: original } = await supabase
      .from('forms')
      .select('config')
      .eq('id', form.id)
      .single();

    const insertData: Record<string, unknown> = {
      id: newId,
      user_id: userId,
      title: `${form.title} (Copy)`,
      description: form.description,
      form_status: 'draft',
      response_count: 0,
      completion_rate: 0,
      last_activity: 'Never',
      has_pattern: form.hasPattern,
      pattern_char: form.patternChar,
      bg_color: form.bgColor,
      config: original?.config || {},
    };

    if (form.workspaceId) {
      insertData.workspace_id = form.workspaceId;
    }

    const { data, error } = await supabase
      .from('forms')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('duplicateForm error:', error.message);
      return null;
    }
    return rowToFormlessItem(data);
  },

  async updateStatus(formId: string, status: FormStatus): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('forms')
      .update({ form_status: status })
      .eq('id', formId);

    if (error) {
      console.error('updateStatus error:', error.message);
      return false;
    }
    return true;
  },

  async getUnassignedForms(userId: string): Promise<FormlessItem[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('user_id', userId)
      .is('workspace_id', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getUnassignedForms error:', error.message);
      return [];
    }
    return (data || []).map(rowToFormlessItem);
  },

  async assignFormToWorkspace(formId: string, workspaceId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('forms')
      .update({ workspace_id: workspaceId })
      .eq('id', formId);

    if (error) {
      console.error('assignFormToWorkspace error:', error.message);
      return false;
    }
    return true;
  },

  async getFormConfig(formId: string): Promise<Record<string, unknown> | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('forms')
      .select('id, title, form_status, config')
      .eq('id', formId)
      .single();

    if (error) {
      console.error('getFormConfig error:', error.message);
      return null;
    }
    // Spread config first, then override with the authoritative DB columns so
    // a stale "status" field inside the config JSON never wins.
    const cfg = (data.config as Record<string, unknown>) || {};
    const { status: _ignored, id: _id, name: _name, ...safeConfig } = cfg as Record<string, unknown>;
    return { ...safeConfig, id: data.id, name: data.title, status: data.form_status };
  },

  async saveFormConfig(formId: string, name: string, status: string, config: Record<string, unknown>): Promise<boolean> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('forms')
      .update({
        title: name,
        form_status: status as 'draft' | 'active' | 'paused' | 'archived',
        config,
        updated_at: new Date().toISOString(),
      })
      .eq('id', formId)
      .select('id')
      .single();

    if (error) {
      console.error('saveFormConfig error:', error.message, error.details, error.hint);
      return false;
    }
    if (!data) {
      console.error('saveFormConfig: no rows updated for formId', formId);
      return false;
    }
    return true;
  },
};

// ─── Conversations ────────────────────────────────────────────────────────────

export interface ConversationMessage {
  role: 'ai' | 'user';
  message: string;
  field?: string;
  timestamp: string;
}

export interface ConversationRecord {
  id: string;
  formId: string;
  user: string;
  email: string;
  status: 'completed' | 'partial' | 'abandoned';
  sentiment: 'positive' | 'neutral' | 'negative';
  summary: string;
  collectedData: Record<string, string>;
  messages: ConversationMessage[];
  createdAt: string;
  trashedAt?: string;
}

function rowToConversation(row: Record<string, unknown>, msgs: ConversationMessage[] = []): ConversationRecord {
  return {
    id: row.id as string,
    formId: row.form_id as string,
    user: (row.user_name as string) || 'Anonymous',
    email: (row.user_email as string) || '—',
    status: (row.conv_status as 'completed' | 'partial' | 'abandoned') || 'partial',
    sentiment: (row.sentiment as 'positive' | 'neutral' | 'negative') || 'neutral',
    summary: (row.summary as string) || '',
    collectedData: (row.collected_data as Record<string, string>) || {},
    messages: msgs,
    createdAt: row.created_at
      ? new Date(row.created_at as string).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Unknown',
    trashedAt: row.trashed_at
      ? new Date(row.trashed_at as string).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : undefined,
  };
}

export const conversationService = {
  async getConversations(formId: string): Promise<ConversationRecord[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('form_id', formId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getConversations error:', error.message);
      return [];
    }
    return (data || []).map((row) => rowToConversation(row as Record<string, unknown>));
  },

  async getConversationWithMessages(convId: string): Promise<ConversationRecord | null> {
    const supabase = createClient();
    const [convRes, msgsRes] = await Promise.all([
      supabase.from('conversations').select('*').eq('id', convId).single(),
      supabase.from('messages').select('*').eq('conversation_id', convId).order('created_at', { ascending: true }),
    ]);

    if (convRes.error) return null;

    const msgs: ConversationMessage[] = (msgsRes.data || []).map((m) => ({
      role: m.role as 'ai' | 'user',
      message: m.message,
      field: m.field_name || undefined,
      timestamp: new Date(m.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    }));

    return rowToConversation(convRes.data as Record<string, unknown>, msgs);
  },

  async createConversation(formId: string): Promise<string | null> {
    const supabase = createClient();
    const id = `c${Date.now()}`;
    const { error } = await supabase
      .from('conversations')
      .insert({ id, form_id: formId, conv_status: 'partial' });

    if (error) {
      console.error('createConversation error:', error.message);
      return null;
    }
    return id;
  },

  async updateConversation(convId: string, updates: {
    userName?: string;
    userEmail?: string;
    status?: 'completed' | 'partial' | 'abandoned';
    sentiment?: 'positive' | 'neutral' | 'negative';
    summary?: string;
    collectedData?: Record<string, unknown>;
  }): Promise<boolean> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if (updates.userName !== undefined) dbUpdates.user_name = updates.userName;
    if (updates.userEmail !== undefined) dbUpdates.user_email = updates.userEmail;
    if (updates.status !== undefined) dbUpdates.conv_status = updates.status;
    if (updates.sentiment !== undefined) dbUpdates.sentiment = updates.sentiment;
    if (updates.summary !== undefined) dbUpdates.summary = updates.summary;
    if (updates.collectedData !== undefined) dbUpdates.collected_data = updates.collectedData;

    const { error } = await supabase
      .from('conversations')
      .update(dbUpdates)
      .eq('id', convId);

    if (error) {
      console.error('updateConversation error:', error.message);
      return false;
    }
    return true;
  },

  async trashConversation(convId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('conversations')
      .update({ trashed_at: new Date().toISOString() })
      .eq('id', convId);

    if (error) {
      console.error('trashConversation error:', error.message);
      return false;
    }
    return true;
  },

  async restoreConversation(convId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('conversations')
      .update({ trashed_at: null })
      .eq('id', convId);

    if (error) {
      console.error('restoreConversation error:', error.message);
      return false;
    }
    return true;
  },

  async deleteConversation(convId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', convId);

    if (error) {
      console.error('deleteConversation error:', error.message);
      return false;
    }
    return true;
  },

  async getRecentConversations(userId: string, limit = 20): Promise<(ConversationRecord & { formName: string })[]> {
    const supabase = createClient();
    // Get all form IDs for this user
    const { data: formsData } = await supabase
      .from('forms')
      .select('id, title')
      .eq('user_id', userId);

    if (!formsData || formsData.length === 0) return [];

    const formMap = new Map<string, string>(formsData.map((f) => [f.id as string, f.title as string]));
    const formIds = formsData.map((f) => f.id as string);

    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .in('form_id', formIds)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('getRecentConversations error:', error.message);
      return [];
    }

    return (data || []).map((row) => ({
      ...rowToConversation(row as Record<string, unknown>),
      formName: formMap.get(row.form_id as string) || 'Untitled Form',
    }));
  },

  async addMessage(convId: string, role: 'ai' | 'user', message: string, fieldName?: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('messages')
      .insert({ conversation_id: convId, role, message, field_name: fieldName || null });

    if (error) {
      console.error('addMessage error:', error.message);
      return false;
    }
    return true;
  },
};

// ─── Analytics ────────────────────────────────────────────────────────────────

export interface DailyAnalytics {
  date: string;
  label: string;
  completed: number;
  partial: number;
  abandoned: number;
}

export const analyticsService = {
  async getDailyData(formId: string): Promise<DailyAnalytics[]> {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const supabase = createClient();

    // Pull directly from conversations table for accurate real-time data
    const { data: convData, error: convError } = await supabase
      .from('conversations')
      .select('conv_status, created_at')
      .eq('form_id', formId)
      .gte('created_at', ninetyDaysAgo.toISOString())
      .order('created_at', { ascending: true });

    if (convError) {
      console.error('getDailyData conversations error:', convError.message);
    }

    // Build a map from conversations data (source of truth)
    const convMap = new Map<string, { completed: number; partial: number; abandoned: number }>();

    if (convData && convData.length > 0) {
      for (const row of convData) {
        // Use local date string to avoid UTC offset issues
        const d = new Date(row.created_at);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (!convMap.has(dateStr)) {
          convMap.set(dateStr, { completed: 0, partial: 0, abandoned: 0 });
        }
        const entry = convMap.get(dateStr)!;
        const status = (row.conv_status as string) || 'partial';
        if (status === 'completed') entry.completed++;
        else if (status === 'abandoned') entry.abandoned++;
        else entry.partial++;
      }
    } else {
      // Fallback to analytics_daily table if no conversation data
      const fromDateStr = ninetyDaysAgo.toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('analytics_daily')
        .select('*')
        .eq('form_id', formId)
        .gte('date', fromDateStr)
        .order('date', { ascending: true });

      if (error) {
        console.error('getDailyData error:', error.message);
        return [];
      }

      return (data || []).map((row) => ({
        date: row.date,
        label: new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        completed: row.completed || 0,
        partial: row.partial || 0,
        abandoned: row.abandoned || 0,
      }));
    }

    // Convert map to sorted array
    const result: DailyAnalytics[] = [];
    convMap.forEach((counts, dateStr) => {
      result.push({
        date: dateStr,
        label: new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        ...counts,
      });
    });

    result.sort((a, b) => a.date.localeCompare(b.date));
    return result;
  },

  async upsertDaily(formId: string, status: 'completed' | 'partial' | 'abandoned'): Promise<void> {
    const today = new Date().toISOString().split('T')[0];
    const supabase = createClient();

    // First try to get existing row
    const { data: existing, error: fetchError } = await supabase
      .from('analytics_daily')
      .select('id, completed, partial, abandoned')
      .eq('form_id', formId)
      .eq('date', today)
      .maybeSingle();

    if (fetchError) {
      console.error('upsertDaily fetch error:', fetchError.message);
    }

    if (existing) {
      // Update existing row by incrementing the correct counter
      const updates: Record<string, number> = {
        completed: existing.completed || 0,
        partial: existing.partial || 0,
        abandoned: existing.abandoned || 0,
      };
      updates[status] = (updates[status] || 0) + 1;

      const { error: updateError } = await supabase
        .from('analytics_daily')
        .update(updates)
        .eq('id', existing.id);

      if (updateError) {
        console.error('upsertDaily update error:', updateError.message);
      }
    } else {
      // Insert new row
      const newRow: Record<string, unknown> = {
        form_id: formId,
        date: today,
        completed: 0,
        partial: 0,
        abandoned: 0,
      };
      newRow[status] = 1;

      const { error: insertError } = await supabase
        .from('analytics_daily')
        .insert(newRow);

      if (insertError) {
        console.error('upsertDaily insert error:', insertError.message);
      }
    }
  },
};

// ─── Webhooks ─────────────────────────────────────────────────────────────────

export interface FormWebhook {
  id: string;
  formId: string;
  name: string;
  url: string;
  secret: string;
  events: string[];
  isActive: boolean;
  createdAt: string;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  event: string;
  status: 'success' | 'failed' | 'pending';
  responseCode?: number;
  durationMs?: number;
  createdAt: string;
}

function rowToWebhook(row: Record<string, unknown>): FormWebhook {
  return {
    id: row.id as string,
    formId: row.form_id as string,
    name: (row.name as string) || 'Webhook',
    url: row.url as string,
    secret: (row.secret as string) || '',
    events: (row.events as string[]) || ['response.completed'],
    isActive: (row.is_active as boolean) ?? true,
    createdAt: row.created_at
      ? new Date(row.created_at as string).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Unknown',
  };
}

function rowToDelivery(row: Record<string, unknown>): WebhookDelivery {
  return {
    id: row.id as string,
    webhookId: row.webhook_id as string,
    event: row.event as string,
    status: (row.status as 'success' | 'failed' | 'pending') || 'pending',
    responseCode: row.response_code as number | undefined,
    durationMs: row.duration_ms as number | undefined,
    createdAt: row.created_at
      ? new Date(row.created_at as string).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : 'Unknown',
  };
}

export const webhookService = {
  async getWebhooks(formId: string): Promise<FormWebhook[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('form_webhooks')
      .select('*')
      .eq('form_id', formId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('getWebhooks error:', error.message);
      return [];
    }
    return (data || []).map((r) => rowToWebhook(r as Record<string, unknown>));
  },

  async createWebhook(formId: string, userId: string, payload: { name: string; url: string; secret?: string; events: string[] }): Promise<FormWebhook | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('form_webhooks')
      .insert({
        form_id: formId,
        user_id: userId,
        name: payload.name,
        url: payload.url,
        secret: payload.secret || null,
        events: payload.events,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      console.error('createWebhook error:', error.message);
      return null;
    }
    return rowToWebhook(data as Record<string, unknown>);
  },

  async updateWebhook(webhookId: string, updates: Partial<{ name: string; url: string; secret: string; events: string[]; isActive: boolean }>): Promise<boolean> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.url !== undefined) dbUpdates.url = updates.url;
    if (updates.secret !== undefined) dbUpdates.secret = updates.secret;
    if (updates.events !== undefined) dbUpdates.events = updates.events;
    if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;

    const { error } = await supabase
      .from('form_webhooks')
      .update(dbUpdates)
      .eq('id', webhookId);

    if (error) {
      console.error('updateWebhook error:', error.message);
      return false;
    }
    return true;
  },

  async deleteWebhook(webhookId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('form_webhooks')
      .delete()
      .eq('id', webhookId);

    if (error) {
      console.error('deleteWebhook error:', error.message);
      return false;
    }
    return true;
  },

  async getDeliveries(webhookId: string, limit = 20): Promise<WebhookDelivery[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('webhook_deliveries')
      .select('*')
      .eq('webhook_id', webhookId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('getDeliveries error:', error.message);
      return [];
    }
    return (data || []).map((r) => rowToDelivery(r as Record<string, unknown>));
  },

  async logDelivery(webhookId: string, event: string, status: 'success' | 'failed', responseCode?: number, durationMs?: number, errorMessage?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('webhook_deliveries').insert({
      webhook_id: webhookId,
      event,
      status,
      response_code: responseCode || null,
      duration_ms: durationMs || null,
      error_message: errorMessage || null,
    });
  },

  async dispatchWebhooks(formId: string, event: string, payload: Record<string, unknown>): Promise<void> {
    const supabase = createClient();
    const { data: webhooks } = await supabase
      .from('form_webhooks')
      .select('*')
      .eq('form_id', formId)
      .eq('is_active', true)
      .contains('events', [event]);

    if (!webhooks || webhooks.length === 0) return;

    for (const wh of webhooks) {
      const start = Date.now();
      try {
        const body = JSON.stringify({ event, form_id: formId, timestamp: new Date().toISOString(), data: payload });
        const res = await fetch(wh.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Webhook-Event': event },
          body,
          signal: AbortSignal.timeout(10000),
        });
        const duration = Date.now() - start;
        await webhookService.logDelivery(wh.id, event, res.ok ? 'success' : 'failed', res.status, duration);
      } catch (err) {
        const duration = Date.now() - start;
        await webhookService.logDelivery(wh.id, event, 'failed', undefined, duration, String(err));
      }
    }
  },
};
